import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import _uniqueId from 'lodash/uniqueId'
import _has from 'lodash/has'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { GraphDimensions, usePerformanceGraph } from './performanceGraph'
import { useEventListener } from '../../../../../utils/customHooks'

import styles from './styles.module.css'

import { Transaction } from '../../../../../typings/stores/PerformanceStore'
import { DateRange } from '../../../../../typings/dates'
import { get, map } from 'lodash'
import { DateTime } from 'luxon'

interface GraphProps {
  dateRange?: DateRange
  graphData?: Array<Transaction>
  dateType?: string
  displayType?: string
  isLoading: boolean
}

export interface DataPoint {
  date: string
  value: number
}

const formatGraphData = (
  displayType: string,
  data: Array<Transaction>
): DataPoint[] => {
  if (data) {
    return map(data, (d, i) => {
      const date = get(data, `[${i}].transactionDate`)
      const value = parseInt(get(data, `[${i}].${displayType}`), 10)

      return {
        date: DateTime.fromSQL(date).toISODate(),
        value,
      }
    })
  }

  return null
}

const Graph: FunctionComponent<GraphProps> = ({
  dateRange,
  graphData,
  displayType,
  isLoading,
}) => {
  const graphRef = useRef<HTMLDivElement>()
  const customId = _uniqueId('graph-')

  const [dimensions, setDimensions] = useState<GraphDimensions>({
    height: null,
    width: null,
  })

  const updateDimensions = useCallback(() => {
    if (_has(graphRef, 'current') && graphRef.current) {
      const height = graphRef.current.clientHeight
      const width = graphRef.current.clientWidth

      setDimensions({
        height,
        width,
      })
    }
  }, [])

  useEffect(() => {
    updateDimensions()

    return () => {
      // We have to remove this on unmount otherwise it will continue to trigger
      // updateDimensions
      graphRef.current = undefined
    }
  }, [updateDimensions])

  useEventListener('resize', window, updateDimensions, true)

  const formattedData = formatGraphData(displayType, graphData)

  usePerformanceGraph(
    customId,
    dimensions,
    displayType,
    dateRange,
    formattedData
  )

  return (
    <div ref={graphRef} className={styles.container} data-rv="po-data-graph">
      {isLoading ? (
        <FontAwesomeIcon
          icon={['fas', 'spinner']}
          pulse
          className={styles.loading}
        />
      ) : (
        <div id={customId} className={styles.graph} />
      )}
    </div>
  )
}

export default Graph
